/**
 * Verlof-check — ES Module versie
 * Origineel: src/verlof-check.js (niet aanraken)
 *
 * Gebruikt dezelfde eligibility als de engine (isBeschikbaar + dienst-type +
 * Arnhem-weekend vast_rotatiepatroon-ban) en hetzelfde minimum (bezettingsDoel),
 * zodat groen/rood overeenkomt met de onderbezetting die het rooster toont.
 *
 * Vastgelegde (vergrendelde) week: geeft de modal het gegenereerde week-rooster mee
 * (roosterVastgelegd), dan telt de check de WERKELIJK ingeroosterde namen per dienst-slot
 * (minus afwezigen — alle types — en de aanvrager) i.p.v. de regel-lijst.
 */
import { isBeschikbaar, berekenBezettingsDoel } from './rooster-engine.js';

// Spiegelt rooster-engine.js (dagLijstVan): welke dagen een dienst in deze schoolweek draait.
function dagLijstVan(d, type) {
  if (type === 'A' && d.week_a_dagen) return d.week_a_dagen;
  if (type === 'B' && d.week_b_dagen) return d.week_b_dagen;
  if (d.week_a_dagen || d.week_b_dagen) return [];
  return d.dagen || [];
}

// Spiegelt de dienst-type-filtering uit vulSlot/vulWeekendPaar.
function dienstTypeOk(dienstId, mw, dag) {
  if (dienstId === 'dagdienst') return !mw.geen_dagdienst && !mw.geen_dagdienst_dagen?.includes(dag);
  if (dienstId.startsWith('late_dienst') || dienstId === 'flexibel') {
    if (mw.alleen_dagdienst && dag !== 'za' && dag !== 'zo') return false;
    if (mw.geen_late_dienst_dagen?.includes(dag)) return false;
  }
  return true;
}

// Afwezig op deze dag? Spiegelt isBeschikbaar's Set/array-afhandeling (bouwAfwezigheidMap
// levert een Set; oude/handmatige vorm kan een array zijn). Alle types tellen als afwezig.
function isAfwezigOpDag(afwezigheid, naam, dag) {
  const afw = afwezigheid[naam];
  if (!afw) return false;
  return afw instanceof Set ? afw.has(dag) : afw.includes(dag);
}

// weeknummer is VERPLICHT: zonder weeknummer schakelt isBeschikbaar de weekend-rotatie
// stilzwijgend uit (effectiefWeeknummer-pad) — dat mag de check nooit doen.
//
// roosterVastgelegd (optioneel): is dit meegegeven, dan is de week vastgelegd en telt de
// check de werkelijk ingeroosterde namen i.p.v. de regel-lijst. De modal bepaalt zelf de
// vastgelegde-week (isoJaar-lookup) en geeft alleen het rooster door (beslissing a).
export function checkVerlof(regels, afwezigheid, aanvragerKey, gemeenteKey, dag, weeknummer, type = null, roosterVastgelegd = null, vakantieDagen = null) {
  const gemeente = regels.gemeenten[gemeenteKey];
  if (!gemeente) return { ok: false, uitleg: `Gemeente '${gemeenteKey}' niet gevonden.` };

  const koppelGrootte = regels.instellingen?.koppel_grootte || 2;

  const dienstenOpDagRuw = Object.entries(gemeente.diensten)
    .filter(([, d]) => dagLijstVan(d, type).includes(dag));

  // Vakantiestand: de engine bouwt GEEN dagdienst-slot op een vakantiedag (rooster-engine.js:516)
  // en laat de cel bewust leeg. Spiegelt analyseer-week.js:102. Zonder deze skip leest de check die
  // lege cel als onderbezetting en waarschuwt hij vals ("0 beschikbaar, 2 nodig").
  const dienstenOpDag = dienstenOpDagRuw
    .filter(([dienstId]) => !(dienstId === 'dagdienst' && vakantieDagen?.has(dag)));

  if (dienstenOpDag.length === 0) {
    // Twee oorzaken, twee teksten: draaide er sowieso niets, of viel de enige dienst weg
    // doordat de dagdienst in een schoolvakantie vervalt?
    const dagdienstOnderdrukt = dienstenOpDagRuw.length > 0;
    return {
      ok: true,
      personenNodig: 0,
      personenBeschikbaar: null,
      uitleg: dagdienstOnderdrukt
        ? `In ${gemeente.naam} draait op ${dag} alleen een dagdienst, en die vervalt in een schoolvakantie. Verlof heeft geen invloed op de bezetting.`
        : `In ${gemeente.naam} wordt op ${dag} niet gewerkt. Verlof heeft geen invloed op de bezetting.`,
    };
  }

  // ── Vastgelegde-week-pad ────────────────────────────────────────────────────
  // De week ligt vast: tel de WERKELIJK ingeroosterde namen per dienst-slot, minus
  // afwezigen (alle types) en de aanvrager. Laat het regel-gebaseerde pad hieronder
  // ongemoeid (dat geldt voor niet-vastgelegde weken — stap 2).
  if (roosterVastgelegd) {
    const aanvragerNaam = regels.medewerkers[aanvragerKey]?.naam;
    const detail = dienstenOpDag.map(([dienstId, d]) => {
      const nodig = berekenBezettingsDoel(d, false, koppelGrootte);
      // Namenlijst per dienst-slot — late_dienst_dw/_we via hun exacte dienst-key;
      // ontbrekend slot → lege lijst.
      const namen = roosterVastgelegd[gemeenteKey]?.[dienstId]?.[dag] || [];
      const beschikbaar = namen.filter(
        (mwNaam) => mwNaam !== aanvragerNaam && !isAfwezigOpDag(afwezigheid, mwNaam, dag)
      ).length;
      return { dienstId, label: d.label || dienstId, nodig, beschikbaar, ok: beschikbaar >= nodig };
    });

    const ok = detail.every(x => x.ok);
    const binding = detail.reduce((a, b) => (b.beschikbaar - b.nodig) < (a.beschikbaar - a.nodig) ? b : a);
    return {
      ok,
      personenNodig: binding.nodig,
      personenBeschikbaar: binding.beschikbaar,
      diensten: detail,
      uitleg: ok
        ? `Als dit verlof wordt goedgekeurd, blijven er voor ${dag} in ${gemeente.naam} nog ${binding.beschikbaar} medewerkers beschikbaar (nodig: ${binding.nodig}). Veilig goed te keuren.`
        : `Let op: als dit verlof doorgaat, zijn er voor ${dag} in ${gemeente.naam} maar ${binding.beschikbaar} medewerkers beschikbaar, terwijl er ${binding.nodig} nodig zijn. Overleg of zoek vervanging.`,
    };
  }

  // Per dienst: tel eligible-personen met dezelfde regels als de engine.
  const detail = dienstenOpDag.map(([dienstId, d]) => {
    const nodig = berekenBezettingsDoel(d, false, koppelGrootte);
    const beschikbaar = Object.entries(regels.medewerkers).filter(([key, mw]) => {
      if (key === aanvragerKey || key.startsWith('_')) return false;
      if (!isBeschikbaar(key, mw, dag, gemeenteKey, afwezigheid, type, weeknummer, vakantieDagen)) return false;
      if (!dienstTypeOk(dienstId, mw, dag)) return false;
      // Arnhem-weekend vast_rotatiepatroon-ban (vulWeekendPaar + vulSlot-guard, rooster-engine.js)
      if (gemeenteKey === 'arnhem' && (dag === 'za' || dag === 'zo') && mw.vast_rotatiepatroon) return false;
      return true;
    }).length;
    return { dienstId, label: d.label || dienstId, nodig, beschikbaar, ok: beschikbaar >= nodig };
  });

  const ok = detail.every(x => x.ok);
  // Bindende dienst = krapste marge (beschikbaar - nodig); die bepaalt de melding.
  const binding = detail.reduce((a, b) => (b.beschikbaar - b.nodig) < (a.beschikbaar - a.nodig) ? b : a);

  return {
    ok,
    personenNodig: binding.nodig,
    personenBeschikbaar: binding.beschikbaar,
    diensten: detail,
    uitleg: ok
      ? `Als dit verlof wordt goedgekeurd, blijven er voor ${dag} in ${gemeente.naam} nog ${binding.beschikbaar} medewerkers beschikbaar (nodig: ${binding.nodig}). Veilig goed te keuren.`
      : `Let op: als dit verlof doorgaat, zijn er voor ${dag} in ${gemeente.naam} maar ${binding.beschikbaar} medewerkers beschikbaar, terwijl er ${binding.nodig} nodig zijn. Overleg of zoek vervanging.`,
  };
}
